// src/services/staff/StaffService.ts

import { supabase } from '../../shared/services/supabase';
import { EmailSenderIdentity } from '../email/types';

export interface XtaxStaffMember {
  id: string;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  initials: string;
  title?: string;
  photo_url?: string;
  is_active: boolean;
  is_primary: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
  telefon?: string | null;
  durchwahl_in_signatur?: boolean;
}

class StaffService {
  
  /**
   * Lädt alle aktiven XTAX-Mitarbeiter aus der Datenbank
   */
  async getActiveStaff(): Promise<EmailSenderIdentity[]> {
    try {
      const { data, error } = await supabase
        .from('xtax_staff')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Fehler beim Laden der XTAX-Mitarbeiter:', error);
        // Fallback auf Tim Wolter
        return [this.getDefaultSender()];
      }

      if (!data || data.length === 0) {
        console.warn('Keine aktiven XTAX-Mitarbeiter gefunden');
        return [this.getDefaultSender()];
      }

      // Konvertiere zu EmailSenderIdentity Format
      return data.map(staff => this.convertToSenderIdentity(staff));

    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
      return [this.getDefaultSender()];
    }
  }

  /**
   * Lädt einen spezifischen Mitarbeiter anhand der ID
   */
  async getStaffById(id: string): Promise<EmailSenderIdentity | null> {
    try {
      const { data, error } = await supabase
        .from('xtax_staff')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.error('Mitarbeiter nicht gefunden:', error);
        return null;
      }

      return this.convertToSenderIdentity(data);

    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeiters:', error);
      return null;
    }
  }

  /**
   * Lädt den primären Mitarbeiter (Tim Wolter)
   */
  async getPrimaryStaff(): Promise<EmailSenderIdentity> {
    try {
      const { data, error } = await supabase
        .from('xtax_staff')
        .select('*')
        .eq('is_primary', true)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.warn('Primärer Mitarbeiter nicht in DB gefunden, verwende Fallback');
        return this.getDefaultSender();
      }

      return this.convertToSenderIdentity(data);

    } catch (error) {
      console.error('Fehler beim Laden des primären Mitarbeiters:', error);
      return this.getDefaultSender();
    }
  }

  /**
   * Konvertiert XtaxStaffMember zu EmailSenderIdentity
   */
  private convertToSenderIdentity(staff: XtaxStaffMember): EmailSenderIdentity {
    return {
      id: staff.id,
      fullName: staff.full_name,
      firstName: staff.first_name,
      lastName: staff.last_name,
      email: staff.email,
      title: staff.title || 'Geschäftsführender Gesellschafter',
      photoUrl: staff.photo_url,
      isPrimary: staff.is_primary,
      telefon: staff.telefon,
      durchwahlInSignatur: staff.durchwahl_in_signatur
    };
  }

  /**
   * Erstellt einen Standard-Fallback Sender (Tim Wolter)
   * Wird verwendet wenn die Datenbank nicht erreichbar ist
   */
  getDefaultSender(): EmailSenderIdentity {
    return {
      id: 'default-tim-wolter',
      fullName: 'Tim Wolter',
      firstName: 'Tim',
      lastName: 'Wolter',
      email: 'tim.wolter@xtax.de',
      title: 'Geschäftsführender Gesellschafter',
      photoUrl: '/images/staff/tim-wolter.jpg',
      isPrimary: true
      // KEIN phone!
    };
  }

  /**
   * Validiert ob ein Mitarbeiter ein gültiges Foto hat
   */
  async validateSenderPhoto(senderId: string): Promise<{ valid: boolean; error?: string }> {
    try {
      const staff = await this.getStaffById(senderId);
      
      if (!staff) {
        return { 
          valid: false, 
          error: 'Mitarbeiter nicht gefunden' 
        };
      }

      if (!staff.photoUrl) {
        return { 
          valid: false, 
          error: `Kein Foto hinterlegt für ${staff.fullName}. E-Mail-Versand nicht möglich.` 
        };
      }

      // Prüfe ob das Foto tatsächlich existiert (für lokale Dateien)
      if (staff.photoUrl.startsWith('/images/staff/')) {
        const photoExists = await this.checkPhotoExists(staff.photoUrl);
        if (!photoExists) {
          return { 
            valid: false, 
            error: `Foto nicht gefunden: ${staff.photoUrl}. Bitte Foto hochladen für ${staff.fullName}.` 
          };
        }
      }

      return { valid: true };

    } catch (error) {
      return { 
        valid: false, 
        error: `Fehler bei Foto-Validierung: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}` 
      };
    }
  }

  /**
   * Prüft ob eine lokale Foto-Datei existiert
   */
  private async checkPhotoExists(photoPath: string): Promise<boolean> {
    try {
      const response = await fetch(photoPath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Fügt einen neuen Mitarbeiter hinzu
   */
  async addStaffMember(staff: Omit<XtaxStaffMember, 'id' | 'created_at' | 'updated_at'>): Promise<EmailSenderIdentity | null> {
    try {
      const { data, error } = await supabase
        .from('xtax_staff')
        .insert([staff])
        .select()
        .single();

      if (error || !data) {
        console.error('Fehler beim Hinzufügen des Mitarbeiters:', error);
        return null;
      }

      return this.convertToSenderIdentity(data);

    } catch (error) {
      console.error('Fehler beim Hinzufügen des Mitarbeiters:', error);
      return null;
    }
  }

  /**
   * Deaktiviert einen Mitarbeiter
   */
  async deactivateStaff(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('xtax_staff')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('Fehler beim Deaktivieren des Mitarbeiters:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('Fehler beim Deaktivieren des Mitarbeiters:', error);
      return false;
    }
  }

  /**
   * Prüft die Verbindung zur Datenbank
   */
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('xtax_staff')
        .select('id')
        .limit(1);

      if (error) {
        return { 
          success: false, 
          error: error.message 
        };
      }

      return { success: true };

    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  }
}

// Singleton Instance
export const staffService = new StaffService();